import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { InstanceProps, InstancedObjectProps } from '../types';
import { type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { ViewerLayer } from './ViewerLayer';
import { CALLBACK_ORDER_AFTER_CAMERA_UPDATE } from './FrameCallbackOrder';
import { useAbyssViewerContext } from './AbyssViewerContext';

const MIN_INSTANCES_COUNT = 1000;

const DEFAULT_LAYERS = [ViewerLayer.Default, ViewerLayer.SphericalViewerLayer];

type BillboardUpdaterProps = {
  groupReference: RefObject<THREE.Group>;
  hoveredGroupReference: RefObject<THREE.Group>;
  hoveredInnerGroupReference: RefObject<THREE.Group>;
  instances: InstanceProps[];
  instancePoses: Float32Array;
  instancedMeshes: THREE.InstancedMesh[];
};

type TemporaryVariables = {
  groupQuaternion: THREE.Quaternion;
  quaternion: THREE.Quaternion;
  matrix4: THREE.Matrix4;
  translation: THREE.Vector3;
  rotation: THREE.Quaternion;
  scale: THREE.Vector3;
};

const BillboardUpdater = ({
  groupReference,
  hoveredGroupReference,
  hoveredInnerGroupReference,
  instances,
  instancePoses,
  instancedMeshes,
}: BillboardUpdaterProps) => {
  const camera = useThree((state) => state.camera);

  const temporaryVariables = useRef<TemporaryVariables>({
    groupQuaternion: new THREE.Quaternion(),
    quaternion: new THREE.Quaternion(),
    matrix4: new THREE.Matrix4(),
    translation: new THREE.Vector3(),
    rotation: new THREE.Quaternion(),
    scale: new THREE.Vector3(),
  });

  useFrame(() => {
    // always face the camera
    if (!groupReference.current) return;
    const { groupQuaternion, quaternion, matrix4, translation, rotation, scale } =
      temporaryVariables.current;
    groupReference.current.updateMatrix();
    groupReference.current.updateWorldMatrix(false, false);
    groupReference.current.getWorldQuaternion(groupQuaternion);
    groupQuaternion.invert();
    camera.getWorldQuaternion(quaternion).premultiply(groupQuaternion);

    for (let index = 0; index < instances.length + 1; index += 1) {
      translation.fromArray(instancePoses, index * 10 + 0);
      rotation.fromArray(instancePoses, index * 10 + 3);
      scale.fromArray(instancePoses, index * 10 + 7);

      matrix4.compose(translation, quaternion, scale);

      instancedMeshes.forEach((mesh) => mesh.setMatrixAt(index, matrix4));
    }

    instancedMeshes.forEach((mesh) => {
      mesh.instanceMatrix.needsUpdate = true;
    });

    if (!hoveredGroupReference.current) return;
    if (!hoveredInnerGroupReference.current) return;

    hoveredGroupReference.current.updateMatrix();
    hoveredGroupReference.current.updateWorldMatrix(false, false);
    hoveredGroupReference.current.getWorldQuaternion(groupQuaternion);
    groupQuaternion.invert();
    camera
      .getWorldQuaternion(hoveredInnerGroupReference.current.quaternion)
      .premultiply(groupQuaternion);
  }, CALLBACK_ORDER_AFTER_CAMERA_UPDATE);

  return <></>;
};

export const InstancedObject = ({
  id,
  meshes,
  hoveredMeshes,
  instances,
  shouldHandleClick,
  layers = DEFAULT_LAYERS,
  isBillboard = false,
}: InstancedObjectProps) => {
  const { meshPickHelper, hoveredInstance: globalHoveredInstance } = useAbyssViewerContext();

  const invalidate = useThree((state) => state.invalidate);

  const hoveredReference = useRef<
    | {
        index: number;
        instance: InstanceProps;
      }
    | undefined
  >();

  const [hoveredInstance, setHoveredInstance] = useState<
    | {
        index: number;
        instance: InstanceProps;
      }
    | undefined
  >();

  const groupReference = useRef<THREE.Group>(null);
  const hoveredGroupReference = useRef<THREE.Group>(null);
  const hoveredInnerGroupReference = useRef<THREE.Group>(null);

  const maxInstancesCount = useMemo(() => {
    // need one more entry to show hovered instance in picking scene
    return (
      2 ** Math.ceil(Math.log2(Math.ceil((instances.length + 1) / MIN_INSTANCES_COUNT))) *
      MIN_INSTANCES_COUNT
    );
  }, [instances.length]);

  const instanceMatrices = useMemo(
    () => new Float32Array(maxInstancesCount * 16),
    [maxInstancesCount]
  );

  const instancePoses = useMemo(
    // translation, rotation (quaternion), scale
    () => new Float32Array(maxInstancesCount * (3 + 4 + 3)),
    [maxInstancesCount]
  );

  const instancedMeshes = useMemo(() => {
    return meshes.map(({ geometry, material, renderOrder }) => {
      const mesh = new THREE.InstancedMesh(geometry, material, maxInstancesCount);
      if (renderOrder) {
        mesh.renderOrder = renderOrder;
      }
      return mesh;
    });
  }, [meshes, maxInstancesCount]);

  useEffect(() => {
    instancedMeshes.forEach((mesh) => {
      layers.forEach((layer) => {
        mesh.layers.enable(layer);
      });
      mesh.frustumCulled = false;
    });
  }, [instancedMeshes, layers]);

  const hoveredInstanceMeshes = useMemo(() => {
    return hoveredMeshes?.map(({ geometry, material, renderOrder }) => {
      const mesh = new THREE.Mesh(geometry, material);
      if (renderOrder) {
        mesh.renderOrder = renderOrder;
      }
      return mesh;
    });
  }, [hoveredMeshes]);

  useEffect(() => {
    hoveredInstanceMeshes?.forEach((mesh) => {
      layers.forEach((layer) => {
        mesh.layers.enable(layer);
      });
      mesh.frustumCulled = false;
    });
  }, [hoveredInstanceMeshes, layers]);

  const temporaryVariables = useRef({
    translationVec: new THREE.Vector3(),
    scaleVec: new THREE.Vector3(),
    matrix4: new THREE.Matrix4(),
    threeColor: new THREE.Color(),
    quat: new THREE.Quaternion(),
  });

  const updateEntryFromInstance = useCallback(
    (index: number, instance: InstanceProps) => {
      const { position, rotation, scale, color } = instance;

      const { translationVec, scaleVec, matrix4, threeColor, quat } = temporaryVariables.current;

      translationVec.set(...position);

      if (rotation) {
        const euler = new THREE.Euler(...rotation);
        quat.setFromEuler(euler);
      } else {
        quat.identity();
      }
      if (typeof scale === 'number') {
        scaleVec.set(scale, scale, scale);
      } else if (scale) {
        scaleVec.set(...scale);
      } else {
        scaleVec.set(1, 1, 1);
      }
      matrix4.compose(translationVec, quat, scaleVec);
      matrix4.toArray(instanceMatrices, index * 16);

      translationVec.toArray(instancePoses, index * 10 + 0);
      quat.toArray(instancePoses, index * 10 + 3);
      scaleVec.toArray(instancePoses, index * 10 + 7);

      if (!isBillboard) {
        // billboard will update matrices each frame
        matrix4.compose(translationVec, quat, scaleVec);
        instancedMeshes.forEach((mesh) => mesh.setMatrixAt(index, matrix4));
      }

      if (color) {
        threeColor.setHex(color);
        instancedMeshes.forEach((mesh) => {
          if (!mesh) return;
          mesh.setColorAt(index, threeColor);
        });
      }
    },
    [isBillboard, instancedMeshes, instanceMatrices, instancePoses]
  );

  const updateInstance = useCallback(
    (index: number) => {
      if (index >= instances.length) {
        // throw new Error(`Index of instance ${index} is out of bounds ${instances.length}`);
        return;
      }
      const hovered = hoveredReference.current;
      const isHovered = index === hovered?.index;
      if (!isHovered) {
        updateEntryFromInstance(index, instances[index]);
        return;
      }
      // hide instance
      const shouldHide = !!hoveredInstanceMeshes;
      if (shouldHide) {
        updateEntryFromInstance(index, { ...hovered!.instance, scale: 0 });
      } else {
        updateEntryFromInstance(index, hovered!.instance);
      }
      // allow picking helper to show this instance as last instance
      const lastIndex = instances.length;
      updateEntryFromInstance(lastIndex, instances[index]);
    },
    [updateEntryFromInstance, hoveredInstanceMeshes, instances]
  );

  const setMeshesNeedUpdate = useCallback(() => {
    instancedMeshes.forEach((mesh) => {
      mesh.count = instances.length;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }
    });
    invalidate();
  }, [instancedMeshes, instances.length, invalidate]);

  useEffect(() => {
    if (instances.length > 0) {
      updateEntryFromInstance(instances.length, { ...instances[0], scale: 0 });
    }
    instances.forEach((instance, index) => {
      updateInstance(index);
    });
    setMeshesNeedUpdate();
  }, [instances, updateInstance, setMeshesNeedUpdate, updateEntryFromInstance]);

  useEffect(() => {
    const { current } = hoveredReference;
    // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
    let newIndex;
    if (globalHoveredInstance && globalHoveredInstance.groupKey === id) {
      newIndex = globalHoveredInstance.instanceIndex;
      if (newIndex === instances.length) {
        // last entry is hovered entry
        return;
      }
    }
    if (newIndex === current?.index) {
      return;
    }
    if (current) {
      current.instance.onPointerOut?.();
      hoveredReference.current = undefined;
      setHoveredInstance(undefined);
      updateInstance(current.index);
      setMeshesNeedUpdate();
    }
    if (newIndex === undefined) {
      if (instances.length > 0) {
        // hide last element
        updateEntryFromInstance(instances.length, { ...instances[0], scale: 0 });
      }
      setMeshesNeedUpdate();
      return;
    }
    const instance = instances[newIndex];
    const instanceUpdate = instance.onPointerOver?.() ?? {};
    hoveredReference.current = { index: newIndex, instance: { ...instance, ...instanceUpdate } };
    setHoveredInstance(hoveredReference.current);
    updateInstance(newIndex);
    setMeshesNeedUpdate();
  }, [
    id,
    instances,
    globalHoveredInstance,
    updateInstance,
    setMeshesNeedUpdate,
    updateEntryFromInstance,
  ]);

  const onClick = useCallback(
    (instanceIndex: number, event: ThreeEvent<PointerEvent>) => {
      if (shouldHandleClick && !shouldHandleClick(event)) return;

      // last entry is hovered entry
      const index =
        instanceIndex === instances.length ? hoveredReference.current?.index : instanceIndex;
      if (index === undefined) return;

      const instance = instances[instanceIndex];
      if (instance.onClick) {
        instance.onClick(event);
      }
    },
    [instances, shouldHandleClick]
  );

  useEffect(() => {
    if (!meshPickHelper) return () => {};
    const groupKey = id;
    instancedMeshes.forEach((mesh, index) =>
      meshPickHelper?.addInstancedMesh(
        `${groupKey}-${index}`,
        groupKey,
        mesh,
        hoveredReference,
        onClick
      )
    );

    return () => {
      instancedMeshes.forEach((mesh, index) => {
        meshPickHelper?.removeInstancedMesh(`${groupKey}-${index}`);
        mesh.dispose();
      });
    };
  }, [id, instancedMeshes, meshPickHelper, onClick]);

  return (
    <>
      <group
        ref={groupReference}
        matrixAutoUpdate={!isBillboard}
        matrixWorldAutoUpdate={!isBillboard}
        frustumCulled={false}
      >
        {instancedMeshes.map((mesh, index) => (
          <primitive object={mesh} key={index} />
        ))}
      </group>
      {isBillboard && (
        <BillboardUpdater
          groupReference={groupReference}
          hoveredGroupReference={hoveredGroupReference}
          hoveredInnerGroupReference={hoveredInnerGroupReference}
          instances={instances}
          instancePoses={instancePoses}
          instancedMeshes={instancedMeshes}
        />
      )}
      {hoveredInstanceMeshes && hoveredInstance && (
        <group
          ref={hoveredGroupReference}
          position={hoveredInstance.instance.position}
          rotation={hoveredInstance.instance.rotation}
          scale={hoveredInstance.instance.scale}
          matrixAutoUpdate={!isBillboard}
          // matrixWorldAutoUpdate={!isBillboard}
          frustumCulled={false}
        >
          <group ref={hoveredInnerGroupReference}>
            {hoveredInstanceMeshes.map((mesh, index) => (
              <primitive object={mesh} key={index} />
            ))}
          </group>
        </group>
      )}
    </>
  );
};
